import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, 
  Clock, 
  Phone, 
  Mail, 
  Download, 
  Share2, 
  MapPin, 
  AlertCircle,
  Loader,
  XCircle
} from 'lucide-react';

const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(JSON.parse(localStorage.getItem('pendingDetails')) || {});

  const useVerifyPayment = (type) => {
    const [status, setStatus] = useState('verifying');
    
    useEffect(() => {
      let isSubscribed = true;
      let verificationTimeout;
  
      const verifyPayment = async () => {
        const reference = new URLSearchParams(location.search).get('reference');
        console.log(`Starting ${type} verification for reference:`, reference);
        
        if (!reference) return;
  
        try {
          const pendingDetails = JSON.parse(localStorage.getItem('pendingDetails'));
          if (!pendingDetails) {
            console.log('No pending details found');
            if (isSubscribed) setStatus('failed');
            return;
          }
  
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/payment/verify`, {
            reference,
            paymentDetails: pendingDetails
          });
          
          if (isSubscribed) {
            if (response.data.status) {
              console.log('Payment verification successful');
              setStatus('success');
              localStorage.removeItem('pendingDetails');
            } else {
              console.log('Payment verification failed');
              setStatus('failed');
            }
          }
        } catch (error) {
          console.error('Verification error:', error);
          if (isSubscribed) setStatus('failed');
        }
      };
  
      // Add a small delay before verification to prevent rapid duplicate requests
      verificationTimeout = setTimeout(verifyPayment, 100);
  
      return () => {
        isSubscribed = false;
        clearTimeout(verificationTimeout);
      };
    }, [location, type]);
  
    return status;
  };

  const status = useVerifyPayment('booking');
  
  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your booking...</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-600 mb-4">Payment Verification Failed</h2>
          <p className="text-gray-600 mb-6">We couldn't verify your payment. Please try again or contact support.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-green-500 text-white py-2.5 px-6 rounded-lg hover:bg-green-600 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!booking) {
    return navigate('/');
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const addToCalendar = () => {
    const startTime = `${booking.appointmentDate.split('T')[0]}T${booking.appointmentTime}:00`;
    const endTime = new Date(new Date(startTime).getTime() + 60 * 60 * 1000).toISOString();
    
    const event = {
      title: `${booking.service} Appointment`,
      start: startTime,
      end: endTime,
      description: `Your ${booking.service} appointment. Selected services: ${booking.subservices.join(', ')}. Notes: ${booking.notes}`,
      location: 'Our Spa Location'
    };

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${event.start.replace(/[-:]/g, '')}/${event.end.replace(/[-:]/g, '')}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  const shareBooking = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Spa Appointment',
          text: `I have a ${booking.service} appointment on ${formatDate(booking.appointmentDate)} at ${booking.appointmentTime}.`,
          url: window.location.href
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(
        `I have a ${booking.service} appointment on ${formatDate(booking.appointmentDate)} at ${booking.appointmentTime}.`
      );
      alert('Booking details copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 md:py-12 px-3 md:px-6">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-20">
          {/* Header */}
          <div className="bg-green-500 py-4 md:py-6 px-4 md:px-8 text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Booking Confirmed!</h1>
            <p className="text-sm md:text-base text-green-50">Thank you for booking with us. Your appointment details are below.</p>
          </div>

          {/* Content */}
          <div className="p-4 md:p-8 space-y-6 md:space-y-8">
            {/* Booking Details */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
              <div className="flex items-center space-x-3 text-sm md:text-base">
                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0" />
                <span className="break-words">{formatDate(booking.appointmentDate)}</span>
              </div>
              <div className="flex items-center space-x-3 text-sm md:text-base">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0" />
                <span>{booking.appointmentTime}</span>
              </div>
              <div className="flex items-center space-x-3 text-sm md:text-base">
                <Mail className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0" />
                <span className="break-words">{booking.customerEmail}</span>
              </div>
              <div className="flex items-center space-x-3 text-sm md:text-base">
                <Phone className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0" />
                <span>{booking.customerPhone}</span>
              </div>
            </div>

            {/* Services */}
            <div className="bg-gray-50 rounded-lg p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Selected Services</h2>
              <ul className="space-y-2">
                {booking.subservices.map((service, index) => (
                  <li key={index} className="flex items-center space-x-2 text-sm md:text-base">
                    <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <span className="text-lg md:text-xl font-bold">
                  Total Amount: ₦{booking.amount.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 rounded-lg p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 flex items-center">
                <AlertCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 text-blue-500 flex-shrink-0" />
                Important Information
              </h2>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2 text-sm md:text-base">
                  <span className="w-2 h-2 mt-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>Please arrive 10 minutes before your appointment time</span>
                </li>
                <li className="flex items-start space-x-2 text-sm md:text-base">
                  <span className="w-2 h-2 mt-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>Bring any relevant medical information if applicable</span>
                </li>
                <li className="flex items-start space-x-2 text-sm md:text-base">
                  <span className="w-2 h-2 mt-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>You'll receive a reminder email 24 hours before your appointment</span>
                </li>
                <li className="flex items-start space-x-2 text-sm md:text-base">
                  <span className="w-2 h-2 mt-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  <span>To reschedule, please call us at least 24 hours in advance</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <button
                onClick={addToCalendar}
                className="flex-1 bg-green-500 text-white py-2.5 md:py-3 px-4 md:px-6 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2 text-sm md:text-base"
              >
                <Download className="w-4 h-4 md:w-5 md:h-5" />
                <span>Add to Calendar</span>
              </button>
              <button
                onClick={shareBooking}
                className="flex-1 border border-green-500 text-green-500 py-2.5 md:py-3 px-4 md:px-6 rounded-lg hover:bg-green-50 transition-colors flex items-center justify-center space-x-2 text-sm md:text-base"
              >
                <Share2 className="w-4 h-4 md:w-5 md:h-5" />
                <span>Share Booking</span>
              </button>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 flex items-center">
                <MapPin className="w-4 h-4 md:w-5 md:h-5 mr-2 text-gray-500 flex-shrink-0" />
                Need Help?
              </h2>
              <div className="space-y-2 text-gray-600 text-sm md:text-base">
                <p>Contact us at:</p>
                <p className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>(234) 816 2298 183</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;